import { useMemo } from "react";
import { useTranslationApp } from "../../../lib/i18next";
import ModalBasicLayout from "../../Modal/ModalBasicLayout";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import Box from "@mui/material/Box";
import {
  BUTTON_CANCEL,
  BUTTON_SAVE,
  FIELD_KEYWORDS_AND_KEYWORDS_NEGATIVE_REPEATED,
  FIELD_REQUIRED,
  FIELD_MAX_LENGTH,
  FIELD_THIS_KEYWORD_ALREADY_EXIST_IN_YOUR_LIST,
  KEYWORD_MATCH_TYPE_PLACEHOLDER,
  KEYWORD_PLACEHOLDER,
  MODAL_ADD_KEYWORDS_TITLE,
  TYPE_PLACEHOLDER,
} from "../../../i18n/keysTranslations";
import { useForm } from "react-hook-form";
import {
  optionsKeywordMatchType,
  optionsSearchTermKeywordType,
  SEARCH_TERM_KEYWORD_TYPE_NEGATIVE,
  SEARCH_TERM_KEYWORD_TYPE_NORMAL,
} from "../../../utils/options";
import TextField from "../../Form/TextField";
import Select from "../../Form/Select";

const KEYWORD_MAX_LENGTH = 80;

const getKeywordCleaned = (text) => {
  return text.toLowerCase().trim().replace(/["']/g, "");
};

const getKeywordMatchedType = ({ text, matchType }) => {
  switch (matchType) {
    case "exact":
      return `[${text}]`;
    case "phrase":
      return `"${text}"`;
    case "broad":
      return text;
    default:
      return text;
  }
};

const getDefaultKeywordMatchType = (searchTermMatchType) => {
  const normalized = String(searchTermMatchType || "").toUpperCase();
  switch (normalized) {
    case "EXACT":
    case "NEAR_EXACT":
      return "exact";
    case "PHRASE":
    case "NEAR_PHRASE":
      return "phrase";
    case "BROAD":
    case "AI_MAX":
      return "broad";
    default:
      return "broad";
  }
};

const SearchTermKeywordModal = ({
  modalOpen,
  onCloseModal,
  onSubmit: onSubmitModal = () => {},
  currentKeywords = [],
  currentKeywordsNegative = [],
  searchTerm,
  canAddPositiveKeywords = true,
}) => {
  const typeOptions = useMemo(
    () =>
      canAddPositiveKeywords
        ? optionsSearchTermKeywordType
        : optionsSearchTermKeywordType.filter(
            (option) => option.value === SEARCH_TERM_KEYWORD_TYPE_NEGATIVE
          ),
    [canAddPositiveKeywords]
  );
  const defaultType = canAddPositiveKeywords
    ? SEARCH_TERM_KEYWORD_TYPE_NORMAL
    : SEARCH_TERM_KEYWORD_TYPE_NEGATIVE;

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: defaultType,
      matchType: getDefaultKeywordMatchType(searchTerm?.matchType),
      text: getKeywordCleaned(searchTerm?.searchTerm || ""),
    },
  });

  const { t } = useTranslationApp();

  const onSubmit = (data) => {
    const { text, matchType, type } = data;
    const keywordCleaned = getKeywordCleaned(text);
    const keywordMatchedType = getKeywordMatchedType({
      text: keywordCleaned,
      matchType,
    });
    const isNegative = type === SEARCH_TERM_KEYWORD_TYPE_NEGATIVE;
    const targetList = isNegative ? currentKeywordsNegative : currentKeywords;
    const oppositeList = isNegative ? currentKeywords : currentKeywordsNegative;

    if (targetList.includes(keywordMatchedType)) {
      setError("text", {
        message: t(FIELD_THIS_KEYWORD_ALREADY_EXIST_IN_YOUR_LIST),
      });
      return;
    }

    if (oppositeList.includes(keywordMatchedType)) {
      setError("text", {
        message: t(FIELD_KEYWORDS_AND_KEYWORDS_NEGATIVE_REPEATED, {
          value: keywordMatchedType,
        }),
      });
      return;
    }

    onSubmitModal({ keyword: keywordMatchedType, type });
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="xs"
      title={t(MODAL_ADD_KEYWORDS_TITLE)}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Select
          name="type"
          label={t(TYPE_PLACEHOLDER)}
          control={control}
          variant="filled"
          fullWidth
          doTranslate={true}
          options={typeOptions}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <Select
          name="matchType"
          label={t(KEYWORD_MATCH_TYPE_PLACEHOLDER)}
          control={control}
          variant="filled"
          fullWidth
          doTranslate={true}
          options={optionsKeywordMatchType}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <TextField
          name="text"
          label={t(KEYWORD_PLACEHOLDER)}
          fullWidth
          variant="filled"
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) =>
              getKeywordCleaned(value).length <= KEYWORD_MAX_LENGTH ||
              t(FIELD_MAX_LENGTH, { value: KEYWORD_MAX_LENGTH }),
          }}
          errors={errors}
        />
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_CANCEL,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            type: "button",
            onClick: handleSubmit(onSubmit),
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default SearchTermKeywordModal;
